import React from 'react'

import { makeStyles, useTheme, useMediaQuery, Grid, FilePreview, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'
import { InlineEditableMedia, InlineEditableText } from '../../inlineEditable'
import { usePresentation } from '../../../context'

const useStyles = makeStyles(
  (_theme: Theme) => {
    return {
      root: {},
      content: {},
      imageWrapper: {
        display: 'flex',
        justifyContent: 'center',
        '& a': {
          display: 'flex',
          justifyContent: 'center'
        }
      },
      imageRoot: {
        maxWidth: '80%',

        '& img': {
          maxHeight: 'none'
        }
      },
      title: {
        whiteSpace: 'pre-wrap'
      }
    }
  },
  { name: 'AwardsBlock' }
)

function AwardsBlock(props: PresentationBlockProps) {
  const classes = useStyles(props)
  const {
    isEditable,
    isSettings,
    presentationIndustry,
    block,
    blockPrefix,
    setFieldValue,
    submitForm,
    WrapperComponent
  } = props
  const { presentationLibrary } = usePresentation()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const responsiveSpacing = isMobile ? 3 : 6

  const { fields, title } = block
  const rows = (fields?.rows ?? []).filter((row) => row?.image || isEditable)

  const columns = getColumns(rows.length, fields?.columns ?? 'auto')

  const handleEditField = (field: string, content: any) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  const getAlignment = (alignment?: string): 'flex-start' | 'center' | 'flex-end' => {
    switch (alignment) {
      case 'top':
        return 'flex-start'
      case 'bottom':
        return 'flex-end'
      default:
        return 'center'
    }
  }

  return (
    <WrapperComponent {...props}>
      <Grid
        container
        spacing={responsiveSpacing}
        justifyContent={'space-between'}
        alignItems={getAlignment(fields?.alignment)}
      >
        {(title || isEditable) && (
          <Grid item md={3}>
            {isEditable ? (
              <InlineEditableText
                variant={'h2'}
                isSubmitOnEnter={true}
                handleEdit={(content) => handleEditField('title', content)}
                placeholderButtonText={'Add Title'}
                classes={{ root: classes.title }}
              >
                {title}
              </InlineEditableText>
            ) : (
              <Typography variant={'h2'} classes={{ root: classes.title }}>
                {title}
              </Typography>
            )}
          </Grid>
        )}
        <Grid item md={title || isEditable ? 9 : 12}>
          <Grid container spacing={3} justifyContent={'space-evenly'} alignItems={'center'}>
            {rows.map((row, index) => {
              return (
                <Grid item {...columns} key={index}>
                  <div className={classes.imageWrapper}>
                    {isEditable || isSettings ? (
                      <InlineEditableMedia
                        classes={{ root: classes.imageRoot }}
                        mediaOptionsProps={{
                          blockPrefix: blockPrefix,
                          fieldSuffix: `rows[${index}].image`
                        }}
                        presentationLibrary={presentationLibrary}
                        file={row.image}
                        handleEdit={(file) => handleEditField(`fields.rows[${index}].image`, file)}
                        isEditable={isEditable}
                        isSettings={isSettings}
                        placeholderButtonText={'Add Image'}
                        presentationIndustry={presentationIndustry}
                      />
                    ) : (
                      row.image && (
                        <>
                          {row.link ? (
                            <a href={row.link} target="_blank" rel="noreferrer">
                              <FilePreview file={row.image} classes={{ root: classes.imageRoot }} />
                            </a>
                          ) : (
                            <FilePreview file={row.image} classes={{ root: classes.imageRoot }} />
                          )}
                        </>
                      )
                    )}
                  </div>
                </Grid>
              )
            })}
          </Grid>
        </Grid>
      </Grid>
    </WrapperComponent>
  )
}

function getColumns(argRows: number, columns: string) {
  let rows = argRows
  if (columns === '2') {
    rows = 2
  } else if (columns === '3') {
    rows = 3
  }

  switch (rows) {
    case 1:
      return {
        xs: 12 as const,
        sm: 12 as const,
        md: 12 as const
      }
    case 2:
      return {
        xs: 6 as const,
        sm: 6 as const,
        md: 6 as const
      }
    case 3:
      return {
        xs: 6 as const,
        sm: 4 as const,
        md: 4 as const
      }
    default:
      return {
        xs: 6 as const,
        sm: 3 as const,
        md: 3 as const
      }
  }
}

export default React.memo(AwardsBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
